// Pages get breadcrumbs via the shells' `crumbs` prop, not by rendering this
// directly — usage rules in ./AGENTS.md.
import { Box, ChevronRightIcon, Row, Text } from "@hightouch/htui";
import { FC, Fragment } from "react";

import { RouterLink, useLocation } from "src/router";
import { TextWithTooltip } from "src/components/text-with-tooltip";
import { resolveBreadcrumbIcon } from "./breadcrumb-icon-map";
import { Crumb } from "./types";

type BreadcrumbsProps = {
  crumbs: Crumb[];
  /**
   * Prefix the first crumb with the page's icon (see breadcrumb-icon-map).
   * Leave off in drawers and other embedded contexts.
   * @default false
   */
  showIcon?: boolean;
};

export const Breadcrumbs: FC<BreadcrumbsProps> = ({
  crumbs,
  showIcon = false,
}) => {
  const location = useLocation();
  const PageIcon = showIcon
    ? resolveBreadcrumbIcon(location.pathname)
    : undefined;

  return (
    <Row
      as="nav"
      aria-label="Breadcrumb"
      align="center"
      gap={1}
      overflow="hidden"
      minWidth={0}
      fontWeight="normal"
    >
      {crumbs.map(({ label, link }, index) => {
        // Root crumb never truncates; deeper crumbs absorb the squeeze.
        const isRoot = index === 0;
        const icon = isRoot && PageIcon && (
          <Box
            as={PageIcon}
            aria-hidden
            boxSize={3}
            color="text.tertiary"
            flexShrink={0}
            _groupHover={link ? { color: "primary.base" } : undefined}
          />
        );
        return (
          <Fragment key={`${label}-${index}`}>
            {link ? (
              <Box
                as={RouterLink}
                to={link}
                className="group"
                minWidth={0}
                overflow="hidden"
                flexShrink={isRoot ? 0 : undefined}
              >
                <Row
                  align="center"
                  gap={1}
                  minWidth={0}
                  color="text.secondary"
                  _groupHover={{ color: "primary.text" }}
                >
                  {icon}
                  <CrumbLabel label={label} />
                </Row>
              </Box>
            ) : (
              <Row
                aria-current="page"
                align="center"
                gap={1}
                minWidth={0}
                color="text.primary"
                flexShrink={isRoot ? 0 : undefined}
              >
                {icon}
                <CrumbLabel label={label} />
              </Row>
            )}
            {index < crumbs.length - 1 && (
              <Box
                as={ChevronRightIcon}
                aria-hidden
                boxSize={3}
                color="text.disabled"
                flexShrink={0}
              />
            )}
          </Fragment>
        );
      })}
    </Row>
  );
};

const CrumbLabel: FC<{ label: Crumb["label"] }> = ({ label }) => (
  <Box minWidth={0} overflow="hidden">
    {typeof label === "string" ? (
      <TextWithTooltip
        textOverflow="ellipsis"
        whiteSpace="nowrap"
        size="sm"
        color="inherit"
      >
        {label}
      </TextWithTooltip>
    ) : (
      <Text isTruncated size="sm" color="inherit">
        {label}
      </Text>
    )}
  </Box>
);
